/**
 * Secrets Collective — scroll-lock safety net.
 *
 * Horizon locks page scrolling by owner: a drawer calls lockScroll(panel), and
 * the lock lifts when every owner has released it or has left the document.
 * Stale owners are pruned inside syncScrollLock, which only runs when its
 * MutationObserver sees a DOM change.
 *
 * That leaves one gap. The cart drawer is morphed rather than replaced, so the
 * panel node that took the lock can be swapped out before the panel node that
 * releases it — different node, so the original owner is never released. It is
 * disconnected, so a later DOM change would prune it, but if the page then goes
 * quiet nothing triggers the prune and the lock stays on with no drawer open.
 * The page is left unable to scroll, and because a modal <dialog> also owns the
 * top layer, unable to be clicked either.
 *
 * This does not reach into Horizon's state. It nudges the DOM so Horizon's own
 * observer runs and prunes its own stale owners. Only if the lock survives that
 * — meaning a live owner is genuinely holding it while nothing is open — does
 * it clear the attribute directly.
 *
 * Deliberately conservative: it never acts while a dialog or drawer really is
 * open, so a legitimate lock is untouched.
 */

const LOCK_ATTR = 'scroll-lock';

/** Anything that has a legitimate claim on the lock right now. */
function somethingIsOpen() {
  if (document.querySelector('dialog[open]')) return true;
  if (document.querySelector('.sc-drawer:not([hidden])')) return true;
  if (document.querySelector('[aria-expanded="true"][aria-controls]')) {
    // A disclosure being open is not itself a lock owner, so only treat it as
    // one when it controls something that is actually on screen.
    for (const trigger of document.querySelectorAll('[aria-expanded="true"][aria-controls]')) {
      const target = document.getElementById(trigger.getAttribute('aria-controls'));
      if (target && target.getBoundingClientRect().height > 0) return true;
    }
  }
  return false;
}

function locked() {
  return document.documentElement.hasAttribute(LOCK_ATTR);
}

/**
 * A childList change on <html>, which is what Horizon's observer watches.
 * Appending and removing a comment is invisible and triggers the prune.
 */
function nudge() {
  const marker = document.createComment('sc-scroll-guard');
  document.documentElement.appendChild(marker);
  marker.remove();
}

let checking = false;

function check() {
  if (checking) return;
  if (!locked() || somethingIsOpen()) return;

  checking = true;
  nudge();

  // Give Horizon its own microtask plus a frame to release the lock properly.
  requestAnimationFrame(() => {
    setTimeout(() => {
      checking = false;
      if (!locked() || somethingIsOpen()) return;

      // Still locked with nothing open: a live owner is holding it that will
      // never let go. Releasing the page is better than stranding the customer.
      document.documentElement.removeAttribute(LOCK_ATTR);
      if (window.Shopify?.designMode) {
        console.warn('[sc-scroll-guard] released a stuck scroll lock');
      }
    }, 250);
  });
}

// Cart activity is when the drawer morphs, so that is when the gap opens.
for (const evt of ['cart:update', 'CartLinesUpdate', 'pointerup', 'keyup']) {
  document.addEventListener(evt, () => setTimeout(check, 400), { passive: true });
}

// A closing dialog is the other moment an owner can be left behind.
document.addEventListener(
  'close',
  () => setTimeout(check, 400),
  { capture: true, passive: true }
);

// Back/forward cache restores can bring back a locked document.
window.addEventListener('pageshow', () => setTimeout(check, 400));
